import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@store'
import { postService } from '@services/api/posts'
import apiClient from '@services/api/client'
import toast from 'react-hot-toast'
import avatarPlaceholder from '@assets/images/image_01.png'
import { storageURL, mediaUrl } from '@config'
import { encodeId } from '@utils/idCipher'

const CONDITION_LABELS = {
  neuf: 'Neuf', scelle: 'Scellé', reconditionne: 'Reconditionné',
  quasi_neuf: 'Quasi neuf', excellent: 'Excellent', bon: 'Bon état', moyen: 'État moyen', usage: 'Usagé',
}

const mapPost = (p) => {
  const prixNum     = parseFloat(p.prix) || 0
  const newPriceNum = parseFloat(p.new_price) || 0
  const discount    = newPriceNum > prixNum && newPriceNum > 0
    ? Math.round((1 - prixNum / newPriceNum) * 100) : 0
  return {
    id:                  p.post_id,
    name:                p.libele,
    price:               p.prix ? `${prixNum.toLocaleString('fr-FR')} FCFA` : '',
    oldPrice:            newPriceNum > prixNum ? `${newPriceNum.toLocaleString('fr-FR')} FCFA` : null,
    discount,
    etat_appareil:       p.etat_appareil || '',
    etat_appareil_label: CONDITION_LABELS[p.etat_appareil] || '',
    image:               (() => { const f = p.fichiers?.find(f => f.is_main) ?? p.fichiers?.[0]; return f?.chemin ? mediaUrl(f.chemin) : null })(),
    views:               p.vues || 0,
    likes:               p.post_likes_count || 0,
    date:                p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR') : '',
    status:              p.status,
  }
}

const mapVideo = (p) => {
  const videoFile = p.fichiers?.find(f => /\.(mp4|mov|avi|webm)$/i.test(f.chemin))
  const thumbFile = p.fichiers?.find(f => /\.(jpg|jpeg|png|gif|webp)$/i.test(f.chemin))
  return {
    id:          p.post_id,
    name:        p.libele,
    description: p.description,
    videoUrl:    videoFile ? mediaUrl(videoFile.chemin) : null,
    thumbnail:   thumbFile ? mediaUrl(thumbFile.chemin) : null,
    views:       p.vues || 0,
    likes:       p.post_likes_count || 0,
    date:        p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR') : '',
    status:      p.status,
  }
}

const mapBanner = (p) => ({
  id: p.post_id,
  name: p.libele,
  description: p.description,
  link: p.siteWeb,
  image: p.fichiers?.[0]?.chemin ? mediaUrl(p.fichiers[0].chemin) : null,
  date: p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR') : '',
  status: p.status,
})

function MyDashboardPage() {
  const { user, userType } = useAuthStore()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('articles') // articles, banners, videos
  const [articleFilter, setArticleFilter] = useState('all') // all, online, completed, archived
  const [bannerFilter, setBannerFilter] = useState('all') // all, online, completed, archived
  const [videoFilter, setVideoFilter] = useState('all') // all, online, completed, archived
  const [myProducts, setMyProducts] = useState([])
  const [myBanners, setMyBanners] = useState([])
  const [myVideos, setMyVideos] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingBanners, setLoadingBanners] = useState(false)
  const [loadingVideos, setLoadingVideos] = useState(false)
  const [isFreeTrialActive, setIsFreeTrialActive] = useState(false)
  const [freeTrialDaysLeft, setFreeTrialDaysLeft] = useState(0)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [productToDelete, setProductToDelete] = useState(null)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [productToArchive, setProductToArchive] = useState(null)
  const [isUnarchiving, setIsUnarchiving] = useState(false)
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalViews: 0,
    totalLikes: 0,
    totalComments: 0,
    totalSubscribers: 0
  })

  // États pour le profil
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: '',
    location: '',
    website: ''
  })

  // États pour les paramètres
  const [settingsData, setSettingsData] = useState({
    notifications: true,
    emailNotifications: true,
    privateProfile: false,
    showEmail: false,
    showPhone: true
  })

  useEffect(() => {
    if (user?.id || user?.client_id) {
      fetchMyProducts()
      checkFreeTrial()
      if (userType === 'admin') fetchSubscribersCount()
    }
  }, [user?.id, user?.client_id])

  // Charger bannières et vidéos quand on change d'onglet
  useEffect(() => {
    if (activeTab === 'banners' && myBanners.length === 0 && user) {
      fetchMyBanners()
    } else if (activeTab === 'videos' && myVideos.length === 0 && user) {
      fetchMyVideos()
    }
  }, [activeTab, user])

  const fetchMyBanners = async () => {
    try {
      setLoadingBanners(true)
      const clientId = user?.id || user?.client_id
      const result = await postService.getPosts({ type: 3, post_client_id: clientId })
      const raw = result.data || []
      setMyBanners(Array.isArray(raw) ? raw.map(mapBanner) : [])
    } catch (error) {
      console.error('Erreur lors du chargement des bannières:', error)
    } finally {
      setLoadingBanners(false)
    }
  }

  const fetchMyVideos = async () => {
    try {
      setLoadingVideos(true)
      const clientId = user?.id || user?.client_id
      const result = await postService.getPosts({ type: 1, post_client_id: clientId })
      const raw = result.data || []
      setMyVideos(Array.isArray(raw) ? raw.map(mapVideo) : [])
    } catch (error) {
      console.error('Erreur lors du chargement des vidéos:', error)
    } finally {
      setLoadingVideos(false)
    }
  }

  const fetchSubscribersCount = async () => {
    try {
      const r = await apiClient.get('/clients/count')
      setStats(prev => ({ ...prev, totalSubscribers: r.count || 0 }))
    } catch {
      setStats(prev => ({ ...prev, totalSubscribers: 0 }))
    }
  }

  const checkFreeTrial = () => {
    const createdDate = user?.created_at ? new Date(user.created_at) : null
    if (!createdDate) return
    const diffDays = Math.floor((Date.now() - createdDate) / (1000 * 60 * 60 * 24))
    const daysLeft = Math.max(0, 7 - diffDays)
    setIsFreeTrialActive(daysLeft > 0)
    setFreeTrialDaysLeft(daysLeft)
  }

  const fetchMyProducts = async () => {
    try {
      setLoading(true)
      const clientId = user?.id || user?.client_id || 1
      const data = await postService.getPosts({
        type: 2,
        post_client_id: clientId
      })

      const raw = data.data || data.posts || data || []
      const products = Array.isArray(raw) ? raw.map(mapPost) : []
      setMyProducts(products)

      // Calculer les stats (uniquement pour les posts publiés)
      const activeProducts = products.filter(p => p.status === 2)
      const totalViews = activeProducts.reduce((sum, p) => sum + parseInt(p.views || 0), 0)
      const totalLikes = activeProducts.reduce((sum, p) => sum + parseInt(p.likes || 0), 0)
      const totalComments = 0
      
      setStats(prev => ({
        ...prev,
        totalProducts: activeProducts.length,
        totalViews,
        totalLikes,
        totalComments
      }))
    } catch (err) {
      console.error('Erreur:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleEditProduct = (product) => {
    // Rediriger vers la page d'édition avec l'ID du produit
    navigate(`/edit-post/${product.id}`)
  }

  const handleDeleteProduct = async (id) => {
    setProductToDelete(id)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (productToDelete) {
      try {
        await postService.deletePost(productToDelete)
        // Recharger les données selon l'onglet actif
        if (activeTab === 'videos') {
          await fetchMyVideos()
        } else if (activeTab === 'banners') {
          await fetchMyBanners()
        } else {
          await fetchMyProducts()
        }
        toast.success(activeTab === 'videos' ? 'Vidéo supprimée avec succès !' : 'Article supprimé avec succès !')
        setShowDeleteModal(false)
        setProductToDelete(null)
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
        toast.error('Erreur lors de la suppression')
      }
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setProductToDelete(null)
  }

  const handleArchivePost = (id, isArchived) => {
    setProductToArchive(id)
    setIsUnarchiving(isArchived)
    setShowArchiveModal(true)
  }

  const confirmArchive = async () => {
    if (productToArchive) {
      try {
        await postService.archivePost(productToArchive)
        // Recharger les données selon l'onglet actif
        if (activeTab === 'videos') {
          await fetchMyVideos()
        } else if (activeTab === 'banners') {
          await fetchMyBanners()
        } else {
          await fetchMyProducts()
        }
        const itemType = activeTab === 'videos' ? 'Vidéo' : activeTab === 'banners' ? 'Bannière' : 'Article'
        toast.success(`${itemType} ${isUnarchiving ? 'désarchivée' : 'archivée'} avec succès !`)
        setShowArchiveModal(false)
        setProductToArchive(null)
        setIsUnarchiving(false)
      } catch (error) {
        console.error('Erreur lors de l\'archivage:', error)
        toast.error(`Erreur lors de l'${isUnarchiving ? 'désarchivage' : 'archivage'}`)
      }
    }
  }

  const cancelArchive = () => {
    setShowArchiveModal(false)
    setProductToArchive(null)
    setIsUnarchiving(false)
  }

  const handleSaveProfile = () => {
    // TODO: Envoyer au backend
    alert('Profil mis à jour avec succès !')
  }

  const handleSaveSettings = () => {
    // TODO: Envoyer au backend
    alert('Paramètres mis à jour avec succès !')
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full md:w-[90%] lg:w-[80%] mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gradient-ligdi mb-2">Mon Dashboard</h1>
          <p className="text-gray-600">Gérez vos articles et suivez vos statistiques</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-6">
          {/* Statistiques */}
          <div className="card-ligdi flex flex-col items-center justify-center text-center">
            <svg className="w-8 h-8 text-blue-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <div className="text-3xl font-bold text-blue-600 mb-1">{stats.totalProducts}</div>
            <div className="text-sm text-gray-600">Articles</div>
          </div>
          
          <div className="card-ligdi flex flex-col items-center justify-center text-center">
            <svg className="w-8 h-8 text-green-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <div className="text-3xl font-bold text-green-600 mb-1">{stats.totalViews}</div>
            <div className="text-sm text-gray-600">Vues</div>
          </div>
          
          <div className="card-ligdi flex flex-col items-center justify-center text-center">
            <svg className="w-8 h-8 text-red-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <div className="text-3xl font-bold text-red-600 mb-1">{stats.totalLikes}</div>
            <div className="text-sm text-gray-600">J'aime</div>
          </div>

          {/* Carte Commentaires */}
          <div className="card-ligdi flex flex-col items-center justify-center text-center">
            <svg className="w-8 h-8 text-orange-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            <div className="text-3xl font-bold text-orange-600 mb-1">{stats.totalComments}</div>
            <div className="text-sm text-gray-600">Commentaires</div>
          </div>

          {/* Carte Abonnés */}
          <div className="card-ligdi flex flex-col items-center justify-center text-center">
            <svg className="w-8 h-8 text-purple-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <div className="text-3xl font-bold text-purple-600 mb-1">{stats.totalSubscribers || 0}</div>
            <div className="text-sm text-gray-600">Abonnés</div>
          </div>
        </div>

        {/* Onglets principaux: Articles, Bannières, Vidéos */}

        <div className="bg-white rounded-xl shadow-sm p-1 mb-6 flex gap-2">
          <button
            onClick={() => setActiveTab('articles')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 ${
              activeTab === 'articles'
                ? 'bg-[#D4AF37] text-white shadow-md'
                : 'text-[#001F3F] hover:bg-gray-100'
            }`}
          >
            📝 Articles
          </button>
          <button
            onClick={() => setActiveTab('banners')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 ${
              activeTab === 'banners'
                ? 'bg-[#D4AF37] text-white shadow-md'
                : 'text-[#001F3F] hover:bg-gray-100'
            }`}
          >
            🎨 Bannières
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 ${
              activeTab === 'videos'
                ? 'bg-[#D4AF37] text-white shadow-md'
                : 'text-[#001F3F] hover:bg-gray-100'
            }`}
          >
            🎥 Vidéos
          </button>
        </div>

        {/* Onglet Articles */}

        {activeTab === 'articles' && (
        <div className="card-ligdi mb-6 !p-2 sm:!p-6">
          <div>
            <div>
                {/* Sous-onglets pour filtrer les articles */}
                <div className="flex items-center gap-2 mb-6 flex-wrap">
                  <button
                    onClick={() => setArticleFilter('all')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      articleFilter === 'all'
                        ? 'bg-[#D4AF37] text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Tous
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myProducts.length}
                    </span>
                  </button>
                  <button
                    onClick={() => setArticleFilter('online')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      articleFilter === 'online'
                        ? 'bg-[#D4AF37] text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    En ligne
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myProducts.filter(p => p.status === 2).length}
                    </span>
                  </button>
                  <button
                    onClick={() => setArticleFilter('completed')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      articleFilter === 'completed'
                        ? 'bg-[#D4AF37] text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    En attente
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myProducts.filter(p => p.status === 1).length}
                    </span>
                  </button>
                  <button
                    onClick={() => setArticleFilter('archived')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      articleFilter === 'archived'
                        ? 'bg-[#D4AF37] text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    Archivés
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myProducts.filter(p => p.status === 3).length}
                    </span>
                  </button>
                </div>

                <div className="flex items-center justify-between mb-6 gap-3">
                  <h2 className="text-lg sm:text-2xl font-bold text-[#001F3F] leading-tight">
                    Mes Articles
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      ({myProducts.filter(product => {
                        if (articleFilter === 'all') return true
                        if (articleFilter === 'online') return product.status === 2
                        if (articleFilter === 'completed') return product.status === 1
                        if (articleFilter === 'archived') return product.status === 3
                        return true
                      }).length})
                    </span>
                  </h2>
                  <button
                    onClick={() => navigate('/create-post')}
                    className="flex-shrink-0 flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-all shadow-md hover:shadow-lg text-sm sm:text-base"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="whitespace-nowrap">Nouvel article</span>
                  </button>
                </div>

                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                        {/* Image skeleton */}
                        <div className="relative w-full h-40 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse">
                          {/* Boutons action skeleton */}
                          <div className="absolute top-2 right-2 flex gap-2">
                            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
                            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
                            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
                          </div>
                        </div>
                        
                        {/* Content skeleton */}
                        <div className="p-4 space-y-3">
                          {/* Title skeleton */}
                          <div className="h-5 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                          
                          {/* Price skeleton */}
                          <div className="h-6 bg-gray-200 rounded w-2/5 animate-pulse"></div>
                          
                          {/* Stats skeleton */}
                          <div className="flex items-center justify-between pt-1">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1">
                                <div className="w-4 h-4 bg-gray-200 rounded-full animate-pulse"></div>
                                <div className="h-3 bg-gray-200 rounded w-8 animate-pulse"></div>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-4 h-4 bg-gray-200 rounded-full animate-pulse"></div>
                                <div className="h-3 bg-gray-200 rounded w-6 animate-pulse"></div>
                              </div>
                            </div>
                            <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : myProducts.filter(product => {
                  if (articleFilter === 'all') return true
                  if (articleFilter === 'online') return product.status === 2
                  if (articleFilter === 'completed') return product.status === 1
                  if (articleFilter === 'archived') return product.status === 3
                  return true
                }).length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-300">
                    <svg className="w-20 h-20 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                      {articleFilter === 'all' && "Aucun article"}
                      {articleFilter === 'online' && "Aucun article en ligne"}
                      {articleFilter === 'completed' && "Aucun article en attente"}
                      {articleFilter === 'archived' && "Aucun article archivé"}
                    </h3>
                    <p className="text-gray-500 mb-6">
                      {articleFilter === 'all' && "Commencez par publier votre premier article"}
                      {articleFilter === 'online' && "Vous n'avez pas d'articles actuellement en ligne"}
                      {articleFilter === 'completed' && "Vous n'avez pas d'articles en attente de validation"}
                      {articleFilter === 'archived' && "Vous n'avez pas d'articles archivés"}
                    </p>
                    <button
                      onClick={() => navigate('/create-post')}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-all shadow-md hover:shadow-lg"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Créer un article
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {myProducts.filter(product => {
                      if (articleFilter === 'all') return true
                      if (articleFilter === 'online') return product.status === 2
                      if (articleFilter === 'completed') return product.status === 1
                      if (articleFilter === 'archived') return product.status === 3
                      return true
                    }).map((product) => (
                      <div key={product.id} className={`bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all flex flex-col ${product.status === 3 ? 'opacity-60 grayscale' : ''}`}>
                        <div className="relative bg-white">
                          <div className="w-full h-56 flex items-center justify-center">
                            <img
                              src={product.image || avatarPlaceholder}
                              alt={product.name}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.onerror = null; e.target.src = avatarPlaceholder }}
                            />
                          </div>
                          {/* Badge de statut */}
                          <div className="absolute top-2 left-2 flex flex-col gap-1">
                            {product.status === 2 && <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">EN LIGNE</span>}
                            {product.status === 1 && <span className="bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded">EN ATTENTE</span>}
                            {product.status === 0 && <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">REJETÉ</span>}
                            {product.status === 3 && <span className="bg-gray-600 text-white text-xs font-bold px-3 py-1 rounded shadow-lg">ARCHIVÉ</span>}
                            {product.discount > 0 && <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">-{product.discount}%</span>}
                          </div>
                          {/* Badge état */}
                          {product.etat_appareil_label && (
                            <div className="absolute top-2 right-2">
                              <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded shadow-md">{product.etat_appareil_label}</span>
                            </div>
                          )}
                        </div>
                        <div className="p-4 flex-1 flex flex-col">
                          <h3 className="font-semibold text-[#001F3F] mb-1 line-clamp-2">{product.name}</h3>
                          {product.oldPrice && (
                            <p className="text-gray-400 line-through text-sm mb-0.5">{product.oldPrice}</p>
                          )}
                          <p className="text-red-600 font-bold text-lg mb-3">{product.price}</p>
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                            <div className="flex items-center gap-3">
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {product.views}
                              </span>
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                                {product.likes}
                              </span>
                            </div>
                            <span>{product.date}</span>
                          </div>
                          
                          {/* Boutons d'actions en bas */}
                          <div className="flex gap-2 mt-auto pt-3 border-t">
                            <button
                              onClick={() => navigate(`/product/${encodeId(product.id)}`)}
                              className="flex-1 p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                              title="Voir"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleEditProduct(product)}
                              className="flex-1 p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors flex items-center justify-center gap-1"
                              title="Modifier"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleArchivePost(product.id, product.status === 3)}
                              className="flex-1 p-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition-colors flex items-center justify-center gap-1"
                              title={product.status === 3 ? "Désarchiver" : "Archiver"}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product.id)}
                              className="flex-1 p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-1"
                              title="Supprimer"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>

          </div>
        </div>
        )}

        {/* Onglet Bannières */}

        {activeTab === 'banners' && (
          <div className="card-ligdi mb-6 !p-2 sm:!p-6">
            <div>
              {/* Sous-onglets pour filtrer les bannières */}
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setBannerFilter('all')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      bannerFilter === 'all'
                        ? 'bg-[#D4AF37] text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Tous
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myBanners.length}
                    </span>
                  </button>
                  <button
                    onClick={() => setBannerFilter('online')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      bannerFilter === 'online'
                        ? 'bg-[#D4AF37] text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    En ligne
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myBanners.filter(b => b.status === 2).length}
                    </span>
                  </button>
                  <button
                    onClick={() => setBannerFilter('completed')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      bannerFilter === 'completed'
                        ? 'bg-[#D4AF37] text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    En attente
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myBanners.filter(b => b.status === 1).length}
                    </span>
                  </button>
                  <button
                    onClick={() => setBannerFilter('archived')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      bannerFilter === 'archived'
                        ? 'bg-[#D4AF37] text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    Archivés
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myBanners.filter(b => b.status === 3).length}
                    </span>
                  </button>
                </div>

                <div className='flex w-full items-center justify-between gap-3 mt-4'>
                  <h2 className="text-lg sm:text-2xl font-bold text-[#001F3F] leading-tight">
                    Mes Bannières
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      ({myBanners.length})
                    </span>
                  </h2>
                  <button
                    onClick={() => navigate('/create-banner')}
                    className="flex-shrink-0 flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors shadow-md text-sm sm:text-base"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="whitespace-nowrap">Nouvelle Bannière</span>
                  </button>
                </div>
                
              </div>

              {loadingBanners ? (
                <div className="grid grid-cols-1 gap-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                      <div className="h-48 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse"></div>
                      <div className="p-4 space-y-2">
                        <div className="h-5 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : myBanners.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500 text-lg mb-4">Aucune bannière</p>
                  <button 
                    onClick={() => navigate('/create-banner')}
                    className="px-6 py-3 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors"
                  >
                    Créer votre première bannière
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myBanners.filter(banner => {
                    if (bannerFilter === 'all') return true
                    if (bannerFilter === 'online') return banner.status === 2
                    if (bannerFilter === 'completed') return banner.status === 1
                    if (bannerFilter === 'archived') return banner.status === 3
                    return true
                  }).map((banner) => (
                    <div key={banner.id} className={`bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow ${banner.status === 3 ? 'opacity-60 grayscale' : ''}`}>
                      <div className="relative">
                        {banner.image ? (
                          <img
                            src={banner.image}
                            alt={banner.name || 'Bannière'}
                            className="w-full h-48 object-cover"
                            onError={(e) => {
                              e.target.onerror = null
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200"%3E%3Crect width="400" height="200" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="16" fill="%239ca3af"%3EImage non disponible%3C/text%3E%3C/svg%3E'
                            }}
                          />
                        ) : (
                          <div className="w-full h-48 bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                            Image non disponible
                          </div>
                        )}
                        <div className="absolute top-2 left-2">
                          {banner.status === 2 && <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">EN LIGNE</span>}
                          {banner.status === 1 && <span className="bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded">EN ATTENTE</span>}
                          {banner.status === 3 && <span className="bg-gray-600 text-white text-xs font-bold px-2 py-1 rounded">ARCHIVÉE</span>}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-[#001F3F] text-lg mb-1">{banner.name}</h3>
                        {banner.description && (
                          <p className="text-gray-600 text-sm mb-2 line-clamp-2">{banner.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {banner.date}
                          </span>
                          {banner.link && (
                            <a href={banner.link} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 text-blue-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              Lien
                            </a>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/banner/${encodeId(banner.id)}`)}
                            className="flex-1 p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors flex items-center justify-center"
                            title="Voir"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => navigate(`/edit-banner/${banner.id}`)}
                            className="flex-1 p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center"
                            title="Modifier"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleArchivePost(banner.id, banner.status === 3)}
                            className="flex-1 p-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition-colors flex items-center justify-center"
                            title={banner.status === 3 ? 'Désarchiver' : 'Archiver'}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(banner.id)}
                            className="flex-1 p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center"
                            title="Supprimer"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Onglet Vidéos */}
        {activeTab === 'videos' && (
          <div className="card-ligdi mb-6 !p-2 sm:!p-6">
            <div>
              {/* Sous-onglets pour filtrer les vidéos */}
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setVideoFilter('all')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      videoFilter === 'all'
                        ? 'bg-[#D4AF37] text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Tous
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myVideos.length}
                    </span>
                  </button>
                  <button
                    onClick={() => setVideoFilter('online')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      videoFilter === 'online'
                        ? 'bg-[#D4AF37] text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    En ligne
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myVideos.filter(v => v.status === 2).length}
                    </span>
                  </button>
                  <button
                    onClick={() => setVideoFilter('completed')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      videoFilter === 'completed'
                        ? 'bg-[#D4AF37] text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    En attente
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myVideos.filter(v => v.status === 1).length}
                    </span>
                  </button>
                  <button
                    onClick={() => setVideoFilter('archived')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      videoFilter === 'archived'
                        ? 'bg-[#D4AF37] text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    Archivés
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                      {myVideos.filter(v => v.status === 3).length}
                    </span>
                  </button>
                </div>

                <div className='flex w-full items-center justify-between gap-3 mt-4'>
                  <h2 className="text-lg sm:text-2xl font-bold text-[#001F3F] leading-tight">
                    Mes Vidéos
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      ({myVideos.length})
                    </span>
                  </h2>
                  <button
                    onClick={() => navigate('/create-video')}
                    className="flex-shrink-0 flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors shadow-md text-sm sm:text-base"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="whitespace-nowrap">Nouvelle Vidéo</span>
                  </button>
                </div>
                
              </div>

              {loadingVideos ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                      <div className="h-48 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse"></div>
                      <div className="p-4 space-y-2">
                        <div className="h-5 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : myVideos.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500 text-lg mb-4">Aucune vidéo</p>
                  <button 
                    onClick={() => navigate('/create-video')}
                    className="px-6 py-3 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors"
                  >
                    Créer votre première vidéo
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myVideos.filter(video => {
                    if (videoFilter === 'all') return true
                    if (videoFilter === 'online') return video.status === 2
                    if (videoFilter === 'completed') return video.status === 1
                    if (videoFilter === 'archived') return video.status === 3
                    return true
                  }).map((video) => (
                    <div
                      key={video.id}
                      className={`bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow ${
                        video.status === 3 ? 'opacity-60 bg-gray-100' : ''
                      }`}
                    >
                      <div className="relative h-48 bg-gray-900 cursor-pointer" onClick={() => navigate(`/video/${encodeId(video.id)}`)}>
                        {video.thumbnail ? (
                          <img
                            src={video.thumbnail}
                            alt={video.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none' }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-16 h-16 text-white opacity-50" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center hover:bg-black/40 transition-colors">
                          <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-[#D4AF37] ml-1" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>
                        {/* Badge durée ou status */}
                        {video.status === 3 && (
                          <div className="absolute top-2 left-2 bg-gray-600 text-white text-xs font-bold px-2 py-1 rounded">
                            Archivé
                          </div>
                        )}
                        {video.status === 1 && (
                          <div className="absolute top-2 left-2 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded">
                            En attente
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-[#001F3F] text-lg mb-2 line-clamp-2">{video.name}</h3>
                        <p className="text-gray-600 text-sm line-clamp-2 mb-3">{video.description}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {video.views || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"/>
                            </svg>
                            {video.likes || 0}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/video/${encodeId(video.id)}`)
                            }}
                            className="flex-1 p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors flex items-center justify-center gap-1"
                            title="Voir"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/edit-video/${video.id}`)
                            }}
                            className="flex-1 p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                            title="Modifier"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation()
                              handleArchivePost(video.id, video.status === 3)
                            }}
                            className="flex-1 p-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition-colors flex items-center justify-center gap-1"
                            title={video.status === 3 ? "Désarchiver" : "Archiver"}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteProduct(video.id)
                            }}
                            className="flex-1 p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-1"
                            title="Supprimer"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 animate-scaleIn">
            <div className="p-2 sm:p-6">
              <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-center text-[#001F3F] mb-2">Supprimer cet article ?</h3>
              <p className="text-center text-gray-600 mb-6">
                Cette action est irréversible. L'article sera définitivement supprimé de votre compte.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={cancelDelete}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-md"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'archivage/désarchivage */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 animate-scaleIn">
            <div className="p-2 sm:p-6">
              <div className={`flex items-center justify-center w-16 h-16 ${isUnarchiving ? 'bg-blue-100' : 'bg-orange-100'} rounded-full mx-auto mb-4`}>
                <svg className={`w-8 h-8 ${isUnarchiving ? 'text-blue-600' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-center text-[#001F3F] mb-2">
                {isUnarchiving ? 'Désarchiver cet article ?' : 'Archiver cet article ?'}
              </h3>
              <p className="text-center text-gray-600 mb-6">
                {isUnarchiving 
                  ? "L'article sera de nouveau visible dans vos articles en ligne."
                  : "L'article ne sera plus visible publiquement mais vous pourrez le restaurer plus tard."}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={cancelArchive}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmArchive}
                  className={`flex-1 px-4 py-3 text-white font-semibold rounded-lg transition-colors shadow-md ${
                    isUnarchiving 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-orange-600 hover:bg-orange-700'
                  }`}
                >
                  {isUnarchiving ? 'Désarchiver' : 'Archiver'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default MyDashboardPage
